// ======================================================================
// SFV Tech | Admin Witness Management Page
// - View EVERY witness record across all expenses (not just "my" items)
// - Override a witness's status on their behalf (with required remark)
// - Reassign a witness slot to a different user
// Admin-only. Talks to /api/admin-witness/*
// ======================================================================

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, AppBar, Toolbar, IconButton, Typography, Button, Stack,
  Chip, Paper, TextField, MenuItem, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Tooltip
} from "@mui/material";
import { ArrowBack, Logout, Search, EditNote, SwapHoriz } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "notistack";
import api from "../../utils/api";

const ACCENT = "#ff6b00";
const BG = "linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)";

export default function AdminWitnessPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ pending_count: 0, agreed_count: 0, disagreed_count: 0, total_expenses: 0 });
  const [users, setUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionLoading, setActionLoading] = useState(false);

  // Override dialog
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null); // { expense_id, slot, currentEmail }
  const [overrideStatus, setOverrideStatus] = useState("agreed");
  const [overrideRemarks, setOverrideRemarks] = useState("");

  // Reassign dialog
  const [reassignDialog, setReassignDialog] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null); // { expense_id, slot, sender, receiver, otherEmail }
  const [reassignEmail, setReassignEmail] = useState("");

  const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
  const time = (d) => (d ? new Date(d).toLocaleString() : "—");

  const getDisplayName = (email) => {
    const u = users.find((x) => x.email === email);
    return u?.nickname ? u.nickname : email || "—";
  };

  const chipColor = (s) => {
    switch ((s || "").toLowerCase()) {
      case "agreed": return "success";
      case "disagreed": return "error";
      default: return "warning";
    }
  };

  // ---------------- Data Load ----------------
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, sumRes, usrRes] = await Promise.all([
        api.get("/api/admin-witness/all", {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            status: statusFilter,
            q: appliedSearch,
          },
        }),
        api.get("/api/admin-witness/summary"),
        api.get("/api/expense-form/users"),
      ]);
      setRows(listRes?.data?.records ?? []);
      setTotal(listRes?.data?.total ?? 0);
      setSummary(sumRes?.data ?? {});
      setUsers(usrRes?.data ?? []);
    } catch (err) {
      enqueueSnackbar("Failed to load witness records.", { variant: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, appliedSearch, enqueueSnackbar]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ---------------- Override ----------------
  const openOverride = (expense_id, slot, currentEmail) => {
    setOverrideTarget({ expense_id, slot, currentEmail });
    setOverrideStatus("agreed");
    setOverrideRemarks("");
    setOverrideDialog(true);
  };

  const submitOverride = async () => {
    if (overrideStatus === "disagreed" && !overrideRemarks.trim()) {
      enqueueSnackbar("A remark is required when overriding to Disagreed.", { variant: "warning" });
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/api/admin-witness/override/${overrideTarget.expense_id}`, {
        slot: overrideTarget.slot,
        status: overrideStatus,
        remarks: overrideRemarks.trim() || null,
      });
      enqueueSnackbar("Witness status overridden.", { variant: "success" });
      setOverrideDialog(false);
      await loadAll();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to override status.", { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------- Reassign ----------------
  const openReassign = (expense_id, slot, sender_email, receiver_email, otherEmail) => {
    setReassignTarget({ expense_id, slot, sender_email, receiver_email, otherEmail });
    setReassignEmail("");
    setReassignDialog(true);
  };

  const eligibleWitnesses = useMemo(() => {
    if (!reassignTarget) return [];
    return users.filter(
      (u) =>
        u.email !== reassignTarget.sender_email &&
        u.email !== reassignTarget.receiver_email &&
        u.email !== reassignTarget.otherEmail
    );
  }, [users, reassignTarget]);

  const submitReassign = async () => {
    setActionLoading(true);
    try {
      await api.put(`/api/admin-witness/reassign/${reassignTarget.expense_id}`, {
        slot: reassignTarget.slot,
        new_witness_email: reassignEmail || null,
      });
      enqueueSnackbar("Witness reassigned.", { variant: "success" });
      setReassignDialog(false);
      await loadAll();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to reassign witness.", { variant: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------- Search ----------------
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setPage(0);
      setAppliedSearch(search.trim());
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10, color: ACCENT, minHeight: "100vh", background: BG }}>
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: BG, color: "#fff" }}>
      {/* Top Bar */}
      <AppBar position="sticky" sx={{ background: "rgba(0,0,0,0.3)", borderBottom: `2px solid ${ACCENT}` }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight={800}>Admin Witness Management</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={loadAll}
              sx={{ borderColor: ACCENT, color: ACCENT, "&:hover": { borderColor: "#fff", color: "#fff" } }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Logout />}
              onClick={logout}
              sx={{ background: ACCENT, color: "#0b1a33", fontWeight: 700, "&:hover": { opacity: 0.85 } }}
            >
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Summary Counters */}
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {[
            { label: "Pending", value: summary.pending_count, color: ACCENT },
            { label: "Agreed", value: summary.agreed_count, color: "#69f0ae" },
            { label: "Disagreed", value: summary.disagreed_count, color: "#ef5350" },
            { label: "Total Expenses w/ Witnesses", value: summary.total_expenses, color: "#fff" },
          ].map((s) => (
            <Paper key={s.label} sx={{ flex: 1, p: 2, bgcolor: "rgba(255,255,255,0.05)", border: `1px solid ${ACCENT}` }}>
              <Typography variant="subtitle2" sx={{ color: ACCENT, fontWeight: 700, letterSpacing: 0.5 }}>
                {s.label}
              </Typography>
              <Typography variant="h5" fontWeight={900} color={s.color} sx={{ mt: 0.5 }}>
                {s.value ?? 0}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* Search + Filter */}
      <Box sx={{ px: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          size="small"
          fullWidth
          placeholder="Search by purpose, sender, receiver, witness, or expense ID — press Enter"
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: ACCENT }} /> }}
          sx={{
            flex: 1,
            backgroundColor: "rgba(255,255,255,0.05)",
            "& .MuiOutlinedInput-root fieldset": { borderColor: ACCENT },
            input: { color: ACCENT },
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          sx={{
            width: 200,
            backgroundColor: "rgba(255,255,255,0.05)",
            "& .MuiOutlinedInput-root fieldset": { borderColor: ACCENT },
            "& .MuiInputBase-input": { color: ACCENT },
            "& .MuiInputLabel-root": { color: ACCENT },
          }}
        >
          {["all", "pending", "agreed", "disagreed"].map((s) => (
            <MenuItem key={s} value={s}>
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Table */}
      <Box sx={{ p: 3 }}>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto" }}>
          <Table>
            <TableHead sx={{ background: "#161616" }}>
              <TableRow>
                {["Expense", "Purpose", "Sender → Receiver", "Amount", "Witness 1", "Witness 2"].map((h) => (
                  <TableCell key={h} sx={{ color: ACCENT, fontWeight: 800 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.expense_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>#{r.expense_id}</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "#94a3b8" }}>
                      {time(r.expense_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.purpose}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {getDisplayName(r.sender_email)} <span style={{ color: "#8894a6" }}>→</span> {getDisplayName(r.receiver_email)}
                    </Typography>
                  </TableCell>
                  <TableCell>{fmt(r.amount)}</TableCell>

                  {/* Witness 1 */}
                  <TableCell>
                    {r.witness1_email ? (
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{getDisplayName(r.witness1_email)}</Typography>
                        <Chip size="small" label={String(r.witness1_status).toUpperCase()} color={chipColor(r.witness1_status)} sx={{ fontWeight: 700, width: "fit-content" }} />
                        {r.witness1_remarks && (
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>"{r.witness1_remarks}"</Typography>
                        )}
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Override status">
                            <IconButton size="small" sx={{ color: ACCENT }} onClick={() => openOverride(r.expense_id, "witness1", r.witness1_email)}>
                              <EditNote fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reassign witness">
                            <IconButton size="small" sx={{ color: ACCENT }} onClick={() => openReassign(r.expense_id, "witness1", r.sender_email, r.receiver_email, r.witness2_email)}>
                              <SwapHoriz fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ) : (
                      <Button size="small" startIcon={<SwapHoriz />} sx={{ color: ACCENT }} onClick={() => openReassign(r.expense_id, "witness1", r.sender_email, r.receiver_email, r.witness2_email)}>
                        Assign
                      </Button>
                    )}
                  </TableCell>

                  {/* Witness 2 */}
                  <TableCell>
                    {r.witness2_email ? (
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{getDisplayName(r.witness2_email)}</Typography>
                        <Chip size="small" label={String(r.witness2_status).toUpperCase()} color={chipColor(r.witness2_status)} sx={{ fontWeight: 700, width: "fit-content" }} />
                        {r.witness2_remarks && (
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>"{r.witness2_remarks}"</Typography>
                        )}
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Override status">
                            <IconButton size="small" sx={{ color: ACCENT }} onClick={() => openOverride(r.expense_id, "witness2", r.witness2_email)}>
                              <EditNote fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reassign witness">
                            <IconButton size="small" sx={{ color: ACCENT }} onClick={() => openReassign(r.expense_id, "witness2", r.sender_email, r.receiver_email, r.witness1_email)}>
                              <SwapHoriz fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ) : (
                      <Button size="small" startIcon={<SwapHoriz />} sx={{ color: ACCENT }} onClick={() => openReassign(r.expense_id, "witness2", r.sender_email, r.receiver_email, r.witness1_email)}>
                        Assign
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                    No witness records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {total > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          )}
        </TableContainer>
      </Box>

      {/* Override Dialog */}
      <Dialog open={overrideDialog} onClose={() => setOverrideDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#161616", color: "#fff" }}>Override Witness Status</DialogTitle>
        <DialogContent sx={{ bgcolor: "#1a1a1a", pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: ACCENT }}>
            Forcing {overrideTarget?.slot === "witness1" ? "Witness 1" : "Witness 2"}'s
            ({getDisplayName(overrideTarget?.currentEmail)}) response on Expense #{overrideTarget?.expense_id}.
          </Typography>
          <TextField
            select
            fullWidth
            label="New Status"
            value={overrideStatus}
            onChange={(e) => setOverrideStatus(e.target.value)}
            sx={{ mb: 2 }}
          >
            {["agreed", "disagreed", "pending"].map((s) => (
              <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Admin remark (required if Disagreed)"
            value={overrideRemarks}
            onChange={(e) => setOverrideRemarks(e.target.value)}
            placeholder="Reason for this override..."
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#1a1a1a" }}>
          <Button onClick={() => setOverrideDialog(false)}>Cancel</Button>
          <Button variant="contained" sx={{ background: ACCENT }} onClick={submitOverride} disabled={actionLoading}>
            {actionLoading ? "Saving..." : "Confirm Override"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialog} onClose={() => setReassignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#161616", color: "#fff" }}>Reassign Witness</DialogTitle>
        <DialogContent sx={{ bgcolor: "#1a1a1a", pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: ACCENT }}>
            Choose a new {reassignTarget?.slot === "witness1" ? "Witness 1" : "Witness 2"} for Expense #{reassignTarget?.expense_id}.
            This resets that slot to Pending.
          </Typography>
          <TextField
            select
            fullWidth
            label="New Witness"
            value={reassignEmail}
            onChange={(e) => setReassignEmail(e.target.value)}
          >
            <MenuItem value="">— Remove witness —</MenuItem>
            {eligibleWitnesses.map((u) => (
              <MenuItem key={u.email} value={u.email}>{u.nickname || u.email}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#1a1a1a" }}>
          <Button onClick={() => setReassignDialog(false)}>Cancel</Button>
          <Button variant="contained" sx={{ background: ACCENT }} onClick={submitReassign} disabled={actionLoading}>
            {actionLoading ? "Saving..." : "Confirm Reassign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
